<template>
  <div v-if="currentTeam || isDemo">
    <DefineNav v-slot="{ mobile }">
      <div
        class="flex grow flex-col gap-y-5 bg-white dark:bg-slate-950"
        :class="[
          mobile ? '' : 'border-r border-gray-200 dark:border-none',
          mobile ? '' : route.meta?.fullScreen && !isExpanded ? 'items-center' : 'px-6 pb-4 pt-2'
        ]"
      >
        <div
          v-if="!mobile"
          class="flex items-center justify-between space-x-3 py-2"
          :class="{ 'mt-2': route.meta.fullScreen }"
        >
          <div
            v-if="user?.is_admin"
            class="text-xl"
          >
            <a :href="adminUrl">
              <img
                class="h-6 w-auto dark:grayscale dark:invert"
                src="/public/img/rmlogo.svg"
              >
            </a>
          </div>
          <router-link
            v-else
            :to="
              !route.params.teamPid
                ? '/'
                : {
                  name: ROUTES.DASHBOARD,
                  params: {
                    teamPid: route.params.teamPid
                  }
                }
            "
          >
            <img
              class="h-6 w-auto"
              src="https://t.rightmessage.com/logo.svg"
              alt="Back to the Dashboard"
            >
          </router-link>

          <div
            v-if="!route.meta.fullScreen"
            class="flex flex-1 items-center justify-between space-x-2"
          >
            <TeamDropdown />
            <button
              type="button"
              class="flex h-[38px] flex-col items-center justify-center rounded bg-gray-100 px-2 py-1 text-gray-500 shadow-sm ring-1 ring-gray-200 transition-all hover:bg-gray-200 hover:text-gray-700 dark:bg-slate-800 dark:text-gray-400 dark:ring-slate-700 dark:hover:bg-slate-700 dark:hover:text-gray-300"
              title="Search ({{ modifierKey }}+K)"
              @click="openCommandPalette"
            >
              <MagnifyingGlassIcon class="h-4 w-4" />
              <div class="mt-1 font-mono text-[10px] leading-tight">
                {{ modifierKeyLabel }}
              </div>
            </button>
          </div>
        </div>
        <div
          v-if="route.meta.fullScreen && !mobile"
          class="flex flex-1 flex-col items-center space-y-4 -mt-2"
        >
          <PublishIndicator />
          <button
            class="flex items-center justify-center"
            @click="
              router.push({
                name: route.meta.backButton || ROUTES.OFFERFUNNELS
              })
            "
          >
            <BackwardIcon class="h-6 w-6 opacity-50 transition duration-100 hover:opacity-100 dark:text-white" />
          </button>
          <div class="absolute bottom-4 left-1/2 -translate-x-1/2 transform">
            <SunIcon
              v-if="!isDark"
              class="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-500"
              aria-hidden="true"
              @click="toggleDark()"
            />
            <MoonIcon
              v-else
              class="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-500"
              aria-hidden="true"
              @click="toggleDark()"
            />
          </div>
        </div>
        <nav
          v-if="!route.meta.fullScreen || mobile || isExpanded"
          class="flex flex-1 flex-col"
        >
          <ul
            role="list"
            class="flex flex-1 flex-col gap-y-7"
          >
            <li>
              <ul
                role="list"
                class="-mx-2 space-y-0.5"
              >
                <!-- Agency management badge -->
                <li
                  v-if="owner?.agency_owner?.name"
                  class="pb-4"
                >
                  <a
                    :href="`mailto:${owner.agency_owner.email}?subject=RightMessage`"
                    class="block rounded-lg bg-sky-200/20 px-3 py-2 dark:bg-gray-800/50 dark:outline dark:outline-1 dark:-outline-offset-1 dark:outline-white/10"
                  >
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      Managed by
                    </div>
                    <div class="mt-1 flex items-center gap-2">
                      <img
                        v-if="owner.agency_owner.logo"
                        :src="owner.agency_owner.logo"
                        alt=""
                        class="h-5 w-5 rounded object-contain"
                      >
                      <span class="text-sm font-medium text-gray-700 dark:text-white">
                        {{ owner.agency_owner.name }}
                      </span>
                    </div>
                  </a>
                </li>
                <li class="pb-8">
                  <PublishButton />
                </li>
                <li
                  v-for="item in navigation"
                  :key="item.name"
                >
                  <div v-if="item.type == 'divider'">
                    <div class="text-primary-600 mb-3 ml-3 mt-10 text-xs font-bold uppercase">
                      {{ item.name }}
                    </div>
                  </div>
                  <router-link
                    v-else
                    :to="
                      item.comingsoon
                        ? {}
                        : {
                          ...item.to,
                          params: {
                            teamPid: route.params.teamPid
                          },
                          ...(item.query || {})
                        }
                    "
                    :class="[
                      isCurrentRoute(item)
                        ? 'text-primary-600 dark:text-primary-200 bg-gray-50 dark:bg-slate-700/50'
                        : 'dark:hover:text-primary-200 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-white/80 dark:hover:bg-slate-700/50',
                      item.permission && !canAccess(item.permission) ? 'opacity-50' : '',
                      item.comingsoon ? 'cursor-not-allowed' : '',
                      'group flex items-center gap-x-3 rounded-md p-2 text-base font-semibold leading-6'
                    ]"
                  >
                    <component
                      :is="item.icon"
                      :class="[
                        isCurrentRoute(item) ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                        'h-5 w-5 shrink-0'
                      ]"
                      aria-hidden="true"
                    />
                    <span class="flex items-center">
                      {{ item.name }}

                      <span
                        v-if="item.aside"
                        class="ml-2.5"
                        v-html="item.aside"
                      />
                    </span>
                    <Tag
                      v-if="item.permission && !canAccess(item.permission)"
                      value="Upgrade"
                      severity="success"
                    />
                    <Tag
                      v-else-if="item.comingsoon && !isDemo"
                      value="Soon"
                      severity="info"
                    />
                    <Tag
                      v-else-if="item.beta"
                      value="Beta"
                      severity="success"
                    />
                  </router-link>

                  <div>
                    <ul
                      role="list"
                      class="ml-9 flex flex-1 flex-col"
                    >
                      <li
                        v-for="child in item.children"
                        :key="child.name"
                      >
                        <a
                          :href="child.href"
                          :class="[
                            isCurrentRoute(child) ? 'font-bold' : 'text-gray-700 opacity-75 hover:text-indigo-600',
                            'group flex gap-x-3 rounded-md p-1 text-xs font-semibold leading-6'
                          ]"
                        >
                          {{ child.name }}
                        </a>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
            </li>
            <li class="mt-auto">
              <div class="flex items-center gap-x-4 lg:gap-x-6">
                <router-link
                  v-if="!isDemo"
                  :to="{ name: ROUTES.SETTINGS }"
                  type="button"
                  class="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
                >
                  <SettingsIcon
                    class="h-5 w-5"
                    aria-hidden="true"
                  />
                </router-link>
                <a
                  href="https://rightmessage.com/help"
                  target="_blank"
                >
                  <QuestionMarkCircleIcon
                    class="h-5 w-5 text-gray-400 hover:text-gray-500"
                    aria-hidden="true"
                  />
                </a>
                <FeatureOs v-if="false" />
                <SunIcon
                  v-if="!isDark"
                  class="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-500"
                  aria-hidden="true"
                  @click="toggleDark()"
                />
                <MoonIcon
                  v-else
                  class="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-500"
                  aria-hidden="true"
                  @click="toggleDark()"
                />

                <!-- Separator -->
                <div
                  class="hidden lg:block lg:h-5 lg:w-px lg:bg-gray-200"
                  aria-hidden="true"
                />

                <!-- Profile dropdown -->
                <Menu
                  v-if="!isDemo"
                  as="div"
                  class="relative"
                >
                  <MenuButton class="-m-1.5 flex items-center p-1.5">
                    <span class="sr-only">Open user menu</span>
                    <img
                      class="h-6 w-6 rounded-full bg-gray-50"
                      :src="profilePhotoUrl"
                      alt=""
                    >
                    <span class="hidden lg:flex lg:items-center">
                      <ChevronDownIcon
                        class="ml-2 h-4 w-4 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </MenuButton>
                  <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="opacity-0 transform scale-95"
                    enter-to-class="opacity-100 transform scale-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="opacity-100 transform scale-100"
                    leave-to-class="opacity-0 transform scale-95"
                  >
                    <MenuItems
                      class="absolute -top-10 right-0 z-10 -mt-28 w-32 origin-bottom-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none dark:bg-slate-800"
                    >
                      <MenuItem
                        v-for="item in userNavigation"
                        :key="item.name"
                        v-slot="{ active }"
                        class="cursor-pointer"
                      >
                        <a
                          :class="[
                            active ? 'bg-gray-50 hover:dark:bg-gray-50' : '',
                            'block px-3 py-1 text-sm leading-6 text-gray-900 dark:text-white/90 dark:hover:text-gray-900'
                          ]"
                          @click="item.action"
                        >
                          {{ item.name }}
                        </a>
                      </MenuItem>
                    </MenuItems>
                  </transition>
                </Menu>
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </DefineNav>
    <!-- mobile -->
    <div class="border-surface-400/50 border-b lg:hidden">
      <div class="flex justify-between p-3">
        <div>
          <HamburgerIcon
            class="h-6 w-6"
            @click="mobileNavVisible = true"
          />
        </div>
      </div>
      <Drawer v-model:visible="mobileNavVisible">
        <template #header>
          <div class="flex items-center gap-2">
            <router-link
              v-if="!isDemo"
              :to="{
                name: ROUTES.DASHBOARD,
                params: {
                  teamPid: route.params.teamPid
                }
              }"
            >
              <img
                class="h-6 w-auto"
                src="https://t.rightmessage.com/logo.svg"
                alt="Back to the Dashboard"
              >
            </router-link>
            <TeamDropdown />
          </div>
        </template>

        <ReuseNav :mobile="true" />
      </Drawer>
    </div>

    <!-- desktop-->
    <div
      id="rm-sidebar"
      :class="[{ 'lg:w-16': route.meta.fullScreen }]"
      class="z-auto hidden transform overflow-y-auto transition-all duration-200 lg:fixed lg:inset-y-0 lg:flex lg:flex-col"
    >
      <div
        v-if="isLocal && !isDemo"
        class="bg-yellow-400 px-4 py-1 text-center text-xs dark:bg-yellow-900 cursor-pointer hover:bg-yellow-500 dark:hover:bg-yellow-800 transition-colors"
        title="Click to copy config to clipboard"
        @click="copyConfigToClipboard"
      >
        Team #{{ currentTeam?.id }}
      </div>
      <ReuseNav :mobile="false" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { createReusableTemplate } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import {
  Cog6ToothIcon as SettingsIcon,
  QuestionMarkCircleIcon,
  SunIcon,
  MoonIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ArrowLeftIcon as BackwardIcon,
  Bars3Icon as HamburgerIcon
} from '@heroicons/vue/24/outline'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ROUTES } from '@/router/routeNames'
import { generateNavigation, generateUserNavigation } from '@/data/sidebarNavigation'
import { RouteLocation } from 'vue-router'
import PublishIndicator from '@/components/common/PublishIndicator.vue'


const [DefineNav, ReuseNav] = createReusableTemplate()

const router = useRouter()
const route = useRoute()
const isLocal = import.meta.env.VITE_APP_ENV === 'development'

const isDark = useDark()
const toggleDark = useToggle(isDark)

const { canAccess: canAccessProxy } = usePermissionStore()

const canAccess = (permission: string) => {
  if (isDemo.value) {
    return true
  }
  return canAccessProxy(permission)
}

const { isExpanded } = useSidebarStore()

const authStore = useAuthStore()
const { user, profilePhotoUrl } = storeToRefs(authStore)
const configStore = useConfigStore()
const { currentTeam, owner, config } = storeToRefs(configStore)
const notificationStore = useNotificationStore()
const teamStore = useTeamStore()
const { isDemo } = storeToRefs(teamStore)

const navigation = generateNavigation
const userNavigation = generateUserNavigation()

const mobileNavVisible = ref(false)

// Command palette (Cmd+K / Ctrl+K)
const { open: openCommandPalette } = useCommandPalette()

// OS detection for keyboard shortcut display
const isMac = computed(() => {
  if (typeof navigator === 'undefined') return false
  return /Mac|iPod|iPhone|iPad/.test(navigator.platform)
})
const modifierKey = computed(() => (isMac.value ? '⌘' : 'Ctrl'))
const modifierKeyLabel = computed(() => (isMac.value ? '⌘-K' : 'CTRL-K'))

const copyConfigToClipboard = async () => {
  try {
    if (!config.value) {
      notificationStore.addError('No config data available to copy')
      return
    }

    const configJson = JSON.stringify(config.value, null, 2)
    await navigator.clipboard.writeText(configJson)

    notificationStore.addNotification('Configuration copied to clipboard')
  } catch (error) {
    console.error('Failed to copy config:', error)
    notificationStore.addError('Failed to copy config to clipboard')
  }
}

watch(
  () => route.fullPath,
  () => {
    mobileNavVisible.value = false
  }
)

const isCurrentRoute = (item: { to: RouteLocation }) => {
  if (!item || !item.to || !item.to.name) return false

  // Check if the current route matches exactly
  if (route.name === item.to.name) return true

  // Check if the current route is a child of the navigation item
  const currentRouteName = route.name
  const itemRouteName = item.to.name

  // Define parent-child relationships
  const parentChildMap = {
    [ROUTES.SEGMENTGROUPS]: [ROUTES.SEGMENTGROUP],
    [ROUTES.QUESTIONS]: [ROUTES.QUESTION],
    [ROUTES.INTEGRATIONS]: [ROUTES.INTEGRATION],
    [ROUTES.OFFERFUNNELS]: [ROUTES.OFFERFUNNEL],
    [ROUTES.WIDGETS]: [ROUTES.WIDGET, ROUTES.CREATEWIDGET],
    [ROUTES.OFFERS]: [ROUTES.OFFER],
    [ROUTES.FORMS]: [ROUTES.FORM],
    [ROUTES.CAMPAIGNS]: [ROUTES.CREATECAMPAIGN, ROUTES.CAMPAIGN],
    [ROUTES.EMAILS]: [ROUTES.EMAIL],
    [ROUTES.GOALS]: [ROUTES.CREATEGOAL],
    [ROUTES.PEOPLE]: [ROUTES.VISITOR_DETAIL]
  }

  // Check if the current route is a child of the navigation item
  return parentChildMap[itemRouteName]?.includes(currentRouteName) || false
}

const adminUrl = computed(() => {
  const backendUrl = import.meta.env.VITE_APP_BACKEND_HOST + '/admin'

  if (owner.value.email !== user.value?.email) {
    return backendUrl + `/users/${owner.value.id}`
  }

  return backendUrl
})
</script>
